import { HonchInstance } from '../config';

// Web Vitals types
interface Metric {
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
}

export class WebVitals {
    private honch: HonchInstance;
    private tracked: Set<string> = new Set();

    constructor(honch: HonchInstance) {
        this.honch = honch;
    }

    start(): void {
        if (typeof window === 'undefined') return;

        this.observeLCP();
        this.observeFID();
        this.observeCLS();
        this.observeTTFB();
        this.observeINP();
    }

    stop(): void {
        // Web Vitals observers are one-time, nothing to clean up
    }

    private trackMetric(metric: Metric): void {
        // Only track each metric once per page load
        if (this.tracked.has(metric.name)) return;
        this.tracked.add(metric.name);

        this.honch.capture('$web_vitals', {
            $metric_name: metric.name,
            $metric_value: metric.value,
            $metric_rating: metric.rating,
        });
    }

    private observeLCP(): void {
        if (!('PerformanceObserver' in window)) return;

        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };

                if (lastEntry) {
                    const value = lastEntry.startTime;
                    this.trackMetric({
                        name: 'LCP',
                        value,
                        rating: value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor',
                    });
                }
            });

            observer.observe({ type: 'largest-contentful-paint', buffered: true });
        } catch (_e) {
            // Observer not supported
        }
    }

    private observeFID(): void {
        if (!('PerformanceObserver' in window)) return;

        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const firstEntry = entries[0] as PerformanceEntry & { processingStart: number; startTime: number };

                if (firstEntry) {
                    const value = firstEntry.processingStart - firstEntry.startTime;
                    this.trackMetric({
                        name: 'FID',
                        value,
                        rating: value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor',
                    });
                }
            });

            observer.observe({ type: 'first-input', buffered: true });
        } catch (_e) {
            // Observer not supported
        }
    }

    private observeCLS(): void {
        if (!('PerformanceObserver' in window)) return;

        let clsValue = 0;

        try {
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries() as (PerformanceEntry & { hadRecentInput: boolean; value: number })[]) {
                    if (!entry.hadRecentInput) {
                        clsValue += entry.value;
                    }
                }
            });

            observer.observe({ type: 'layout-shift', buffered: true });

            // Report CLS on page hide
            const reportCLS = () => {
                this.trackMetric({
                    name: 'CLS',
                    value: clsValue,
                    rating: clsValue <= 0.1 ? 'good' : clsValue <= 0.25 ? 'needs-improvement' : 'poor',
                });
            };

            window.addEventListener('pagehide', reportCLS, { once: true });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    reportCLS();
                }
            }, { once: true });
        } catch (_e) {
            // Observer not supported
        }
    }

    private observeTTFB(): void {
        if (typeof window === 'undefined' || !window.performance) return;

        try {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            if (navigation) {
                const value = navigation.responseStart - navigation.requestStart;
                this.trackMetric({
                    name: 'TTFB',
                    value,
                    rating: value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor',
                });
            }
        } catch (_e) {
            // Not supported
        }
    }

    private observeINP(): void {
        if (!('PerformanceObserver' in window)) return;

        const interactions: number[] = [];

        try {
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries() as (PerformanceEntry & { duration: number; interactionId: number })[]) {
                    if (entry.interactionId) {
                        interactions.push(entry.duration);
                    }
                }
            });

            observer.observe({ type: 'event', buffered: true, durationThreshold: 16 } as any);

            // Report INP on page hide
            const reportINP = () => {
                if (interactions.length === 0) return;

                // INP is the 98th percentile of interactions
                interactions.sort((a, b) => a - b);
                const index = Math.floor(interactions.length * 0.98);
                const value = interactions[index] || interactions[interactions.length - 1];

                this.trackMetric({
                    name: 'INP',
                    value,
                    rating: value <= 200 ? 'good' : value <= 500 ? 'needs-improvement' : 'poor',
                });
            };

            window.addEventListener('pagehide', reportINP, { once: true });
        } catch (_e) {
            // Observer not supported
        }
    }
}
